import { derivative, evaluate } from "mathjs";
import GraphModel from "./GraphModel";
import type { Curvy } from "./RelationItem";
import type { GraphPoint } from "./GraphPoint";

export function evaluateRelation(relation: Curvy, xValue: number) {
  const curveType = relation.curveType;
  if (curveType == "formula") {
    return evaluateRelationFormula(relation.formula, xValue);
  }

  const graphModel = new GraphModel(relation.curvePoints);
  return graphModel.valueAtX(xValue);
}

export function evaluateRelationFormula(formula: string, xValue: number) {
  try {
    const val = evaluate(formula, { x: xValue });
    if (typeof val != "number") {
      console.warn("invalid number", val, formula, xValue);
      return 0;
    }
    if (isNaN(val)) return 0;
    if (val == Infinity) return 0;
    if (val == -Infinity) return 0;
    return val;
  } catch (error) {
    console.warn("math error", formula, xValue, error);
    return 0;
  }
}

export function getFormulaPoints(formula: string, stepSize: number, xMin: number, xMax: number) {
  const vals: GraphPoint[] = [];
  let j = 0;
  let lastDerivative = Infinity;
  let formulaDerivative = { evaluate: (val: { x: number }) => val.x, toString: () => "x" };
  try {
    formulaDerivative = derivative(formula, "x");
  } catch (error) {
    console.warn(error);
  }

  const step = stepSize;
  if (step == 0) {
    return [{
      id: 0,
      x: xMin,
      y: evaluateRelationFormula(formula, xMin)
    }];
  } else {
    if (formulaDerivative.toString().includes("x")) {
      // if the derivative is not a constant (e.g. with formula x^3), we need to calculate 
      // the points where the derivative changes
      for (let i = xMin; i <= xMax; i += step) {
        const currentDerivative = formulaDerivative.evaluate({ x: i });
        const isNotLast = i + step <= xMax;
        if (currentDerivative == lastDerivative && isNotLast) {
          continue;
        }
        lastDerivative = currentDerivative;
    
        vals.push({
          id: j++,
          x: i,
          y: evaluateRelationFormula(formula, i)
        });
      }
  
      // remove duplicates
      for (let i = vals.length - 2; i >= 0; i--) {
        if (vals[i + 2]?.y == vals[i + 1]?.y && vals[i + 1]?.y == vals[i]?.y) {
          vals.splice(i + 1, 1);
        }
      }
    } else {
      // if the derivative is a constant, we can just add the min and max values
      // since the function is linear
      vals.push({
        id: 0,
        x: xMin,
        y: evaluateRelationFormula(formula, xMin)
      },
      {
        id: 1,
        x: xMax,
        y: evaluateRelationFormula(formula, xMax)
      });
    }
  
    // add beziers
    for (let i = 0; i < vals.length; i++) {
      if (i != 0) {
        vals[i].bezierIn = {
          x: 0,
          y: 0
        };
      }
      if (i != vals.length - 1) {
        vals[i].bezierOut = {
          x: 0,
          y: 0
        };
      }
    }
  }

  return vals;
}
